import { Anchor, Button } from '@mantine/core';
import type { AxiosError } from 'axios';
import { URLS } from 'consts';
import { getZodSchemaErrorMessages } from 'helpers';
import { useServerErrors } from 'hooks';
import { MailIcon, SignatureSmsIcon } from 'icons';
import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PhoneInput, TextInput } from 'shared/ui/Inputs';
import { z } from 'zod';

import { AuthContainerLoginHeader } from '../../AuthContainerLoginHeader';
import { LoginOptionsDivider } from '../LoginOptionsDivider';
import { SsoLoginMethods } from '../SsoLoginMethods';

type Props = {
  identifier: string;
  isLoading: boolean;
  setIdentifier: (identifier: string) => void;
  mutate: ({ identifier }: { identifier: string }) => void;
  error?: AxiosError | null;
};

export const ChooseLoginMethod = ({
  identifier,
  isLoading,
  setIdentifier,
  mutate,
  error = null,
}: Props) => {
  const { t } = useTranslation(['common', 'user']);
  const [isPhoneMode, setIsPhoneMode] = useState(false);

  const emailSchema = z.object({
    identifier: z
      .string()
      .trim()
      .min(1, { message: t('formErrors.required', { ns: 'common' }) })
      .email({ message: t('formErrors.incorrectEmail', { ns: 'common' }) }),
  });

  const phoneSchema = z.object({
    identifier: z
      .string()
      .trim()
      .min(1, { message: t('formErrors.required', { ns: 'common' }) }),
  });

  const schema = isPhoneMode ? phoneSchema : emailSchema;

  const { getGeneralServerError, getServerFieldError } = useServerErrors();

  const [identifierFieldErrorMessage, setIdentifierFieldErrorMessage] =
    useState('');
  const [serverErrorMessage, setServerErrorMessage] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isLoading && error) {
      const fieldIdentifierError =
        getServerFieldError(error, 'email') ||
        getServerFieldError(error, 'phone');
      const generalServerError = getGeneralServerError(error);

      if (fieldIdentifierError) {
        setIdentifierFieldErrorMessage(fieldIdentifierError);
      } else if (generalServerError) {
        setServerErrorMessage(generalServerError);
      }

      inputRef.current?.focus();
    }
  }, [isLoading, error, getServerFieldError, getGeneralServerError]);

  const handleChangeIdentifier = (e: ChangeEvent<HTMLInputElement>) => {
    setIdentifierFieldErrorMessage('');
    setIdentifier(e.target.value);
  };

  const handleChangePhone = (value: string) => {
    setIdentifierFieldErrorMessage('');
    setIdentifier(`+${value}`);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = getZodSchemaErrorMessages({
      schema,
      fields: { identifier },
    });

    if (errors) {
      setIdentifierFieldErrorMessage(errors.identifier);
      inputRef.current?.focus();
    } else {
      mutate({ identifier });
    }
  };

  const handleToggleMode = () => {
    setIsPhoneMode(prev => !prev);
    setIdentifier('');
    setIdentifierFieldErrorMessage('');
  };

  return (
    <>
      <AuthContainerLoginHeader errorLabel={serverErrorMessage} />
      <form onSubmit={handleSubmit}>
        {isPhoneMode ? (
          <PhoneInput
            label={t('phoneNumber', { ns: 'common' })}
            value={identifier.replace(/^\+/, '')}
            onChange={handleChangePhone}
            error={identifierFieldErrorMessage}
            ref={inputRef}
            inputProps={{ autoFocus: true }}
          />
        ) : (
          <TextInput
            id="identifier"
            autoComplete="email"
            label={t('email', { ns: 'common' })}
            placeholder={t('emailPlaceholder', { ns: 'common' })}
            autoFocus
            value={identifier}
            error={identifierFieldErrorMessage}
            ref={inputRef}
            onChange={handleChangeIdentifier}
          />
        )}

        <Button
          sx={theme => ({
            marginBottom: theme.other.spacing(2),
            fontWeight: 600,
          })}
          type="submit"
          fullWidth
          size="lg"
          loading={isLoading}
        >
          {t('continue', { ns: 'common' })}
        </Button>
      </form>
      {/* TODO: PER-3170 — show password reset for phone mode after backend support */}
      {!isPhoneMode && (
        <Anchor
          sx={{ display: 'flex', justifyContent: 'center' }}
          component={Link}
          to={URLS.requestPasswordReset}
          state={{ email: identifier }}
        >
          {t('dontRememberPassword', { ns: 'user' })}
        </Anchor>
      )}
      <LoginOptionsDivider />
      <SsoLoginMethods />
      <Anchor
        component="button"
        sx={theme => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.other.spacing(1),
          width: '100%',
          marginTop: theme.other.spacing(2),
        })}
        onClick={handleToggleMode}
      >
        {isPhoneMode ? (
          <MailIcon size={16} color="primary" />
        ) : (
          <SignatureSmsIcon size={16} color="primary" />
        )}
        {isPhoneMode
          ? t('loginByEmail', { ns: 'user' })
          : t('loginByPhoneNumber', { ns: 'user' })}
      </Anchor>
    </>
  );
};
